// Shared Welcome Bonus state — one network source for Header badge + Wallet/payout.

import { BALANCE_REFRESH_EVENT } from '@/utils/balanceRefresh';
import {
  claimSignupBonus,
  fetchSignupBonus,
  DAILY_BUDGET_USER_MESSAGE,
  type SignupBonus,
} from './signupBonusService';

export type SignupBonusStoreState = {
  bonus: SignupBonus | null;
  loading: boolean;
  claiming: boolean;
  claimError: string | null;
};

type Listener = () => void;

const listeners = new Set<Listener>();

let state: SignupBonusStoreState = {
  bonus: null,
  loading: true,
  claiming: false,
  claimError: null,
};

let prevStatus: SignupBonus['status'] | null = null;
let inflightLoad: Promise<void> | null = null;
let hasLoadedOnce = false;
let balanceRefreshBound = false;

function emit() {
  listeners.forEach((l) => l());
}

function setState(patch: Partial<SignupBonusStoreState>) {
  state = { ...state, ...patch };
  emit();
}

function applyBonusResult(data: SignupBonus | null) {
  const prev = prevStatus;
  const shouldRefreshBalance =
    (prev === 'active' &&
      (data?.status === 'expired' ||
        data?.status === 'completed' ||
        data?.status === 'in_review')) ||
    (prev === 'in_review' && data?.status === 'completed') ||
    (prev == null && data?.status === 'expired');

  prevStatus = data?.status ?? null;

  let claimError = state.claimError;
  if (data?.status === 'inactive' && data.claimBlockCode === 'DAILY_BUDGET') {
    claimError = data.claimBlockMessage || DAILY_BUDGET_USER_MESSAGE;
  } else if (data?.status !== 'inactive') {
    claimError = null;
  }

  setState({ bonus: data, claimError, loading: false });

  if (shouldRefreshBalance) {
    window.dispatchEvent(new Event(BALANCE_REFRESH_EVENT));
  }
}

async function loadInternal(forceRefresh: boolean) {
  try {
    const data = await fetchSignupBonus(forceRefresh);
    applyBonusResult(data);
  } catch {
    setState({ bonus: null, loading: false });
  }
}

export function getSignupBonusSnapshot(): SignupBonusStoreState {
  return state;
}

export function subscribeSignupBonus(listener: Listener): () => void {
  listeners.add(listener);
  ensureSignupBonusStoreStarted();
  return () => {
    listeners.delete(listener);
  };
}

/** Idempotent boot: first subscriber loads once; binds a single balance-refresh listener. */
export function ensureSignupBonusStoreStarted(): void {
  if (typeof window === 'undefined') return;
  if (!balanceRefreshBound) {
    balanceRefreshBound = true;
    window.addEventListener(BALANCE_REFRESH_EVENT, onBalanceRefresh);
  }
  // Soft: first subscriber loads; later subscribers reuse store / in-flight.
  void loadSignupBonus(false);
}

function onBalanceRefresh() {
  void loadSignupBonus(true);
}

/**
 * Shared load — concurrent callers share one in-flight promise.
 * Soft calls after the first completed load are no-ops; forceRefresh always hits the API.
 */
export function loadSignupBonus(forceRefresh = false): Promise<void> {
  if (inflightLoad) return inflightLoad;
  if (!forceRefresh && hasLoadedOnce) return Promise.resolve();

  if (!hasLoadedOnce) setState({ loading: true });

  inflightLoad = loadInternal(true).finally(() => {
    hasLoadedOnce = true;
    inflightLoad = null;
  });
  return inflightLoad;
}

export async function claimSignupBonusShared(): Promise<SignupBonus | null> {
  setState({ claiming: true, claimError: null });
  try {
    const data = await claimSignupBonus();
    prevStatus = data?.status ?? null;
    setState({ bonus: data, claiming: false });
    window.dispatchEvent(new Event(BALANCE_REFRESH_EVENT));
    return data;
  } catch (err: unknown) {
    const e = err as {
      message?: string;
      error?: string | { message?: string; code?: string };
      data?: { error?: { message?: string; code?: string }; message?: string };
    };
    const code =
      (typeof e.error === 'object' && e.error?.code) || e.data?.error?.code;
    if (code === 'DAILY_BUDGET') {
      setState({ claiming: false, claimError: DAILY_BUDGET_USER_MESSAGE });
      throw err;
    }
    const nested =
      (typeof e.error === 'object' && e.error?.message) ||
      e.data?.error?.message ||
      e.data?.message;
    const msg =
      nested || e.message || 'Unable to activate welcome bonus. Please try again later.';
    setState({ claiming: false, claimError: msg });
    throw err;
  }
}

export function setSignupBonusClaimError(message: string | null) {
  setState({ claimError: message });
}

/** Test helper — reset module singleton between Jest cases. */
export function __resetSignupBonusStoreForTests() {
  if (balanceRefreshBound && typeof window !== 'undefined') {
    window.removeEventListener(BALANCE_REFRESH_EVENT, onBalanceRefresh);
  }
  state = {
    bonus: null,
    loading: true,
    claiming: false,
    claimError: null,
  };
  prevStatus = null;
  inflightLoad = null;
  hasLoadedOnce = false;
  balanceRefreshBound = false;
  listeners.clear();
}
